import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

interface Segment {
  nameEn: string;
  nameFr: string;
  badge?: { icon: string; color: string; bg: string };
  icon: string;
}

const segments: Segment[] = [
  { nameEn: 'EVs', nameFr: 'VÉ', badge: { icon: 'ri-flashlight-fill', color: '#FACC15', bg: 'rgba(250,204,21,0.15)' }, icon: 'ri-car-line' },
  { nameEn: 'Hybrids', nameFr: 'Hybrides', badge: { icon: 'ri-leaf-fill', color: '#22C55E', bg: 'rgba(34,197,94,0.15)' }, icon: 'ri-car-line' },
  { nameEn: 'Gas', nameFr: 'Essence', badge: { icon: 'ri-gas-station-fill', color: '#EF4444', bg: 'rgba(239,68,68,0.15)' }, icon: 'ri-car-line' },
  { nameEn: 'Sedans', nameFr: 'Berlines', icon: 'ri-car-line' },
  { nameEn: 'SUVs', nameFr: 'VUS', icon: 'ri-car-line' },
  { nameEn: 'Pickups', nameFr: 'Camionnettes', icon: 'ri-truck-line' },
  { nameEn: 'Coupes', nameFr: 'Coupés', icon: 'ri-car-line' },
  { nameEn: 'Wagons', nameFr: 'Familiales', icon: 'ri-car-line' },
  { nameEn: 'Hatchbacks', nameFr: 'À hayon', icon: 'ri-car-line' },
  { nameEn: 'Convertibles', nameFr: 'Décapotables', icon: 'ri-car-line' },
  { nameEn: 'Cargo Vans', nameFr: 'Fourgonnettes', icon: 'ri-bus-line' },
  { nameEn: 'Box Trucks', nameFr: 'Camions cube', icon: 'ri-truck-line' },
];

// Keep inline SVG silhouettes — same as before
const silhouettes: Record<string, JSX.Element> = {
  EVs: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M14 44 Q14 38 18 36 L26 24 Q34 16 52 15 Q68 15 80 18 L96 28 Q106 34 107 44 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M28 24 L36 16 L62 15 L72 24" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M72 24 L88 28 L96 36" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="52" y1="15" x2="52" y2="44" stroke="currentColor" strokeWidth="1" strokeOpacity="0.3"/>
      <circle cx="30" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="90" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="30" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="90" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  Hybrids: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M10 44 L10 36 Q10 34 12 34 L18 34 L22 22 Q28 14 48 13 Q68 13 78 16 L88 22 L100 26 Q108 28 110 36 L110 44 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M22 34 L26 20 L52 13 L68 13 L78 22 L88 22" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="26" y1="20" x2="78" y2="20" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M78 20 L88 22 L88 34" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="88" y1="34" x2="100" y2="34" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="96" y="30" width="5" height="4" rx="1" stroke="currentColor" strokeWidth="1.2"/>
      <circle cx="28" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="92" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="28" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="92" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  Gas: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M10 44 L10 36 Q10 33 13 33 L18 33 L24 20 Q32 12 50 11 Q70 11 82 15 L94 22 Q106 28 108 36 L108 44 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M24 33 L28 19 L54 11 L70 11 L82 20" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="28" y1="19" x2="78" y2="19" stroke="currentColor" strokeWidth="1.5"/>
      <line x1="78" y1="19" x2="88" y2="33" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M88 33 L88 27 L102 27 L102 33" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <rect x="103" y="40" width="4" height="2.5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
      <rect x="103" y="43" width="4" height="2.5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
      <circle cx="28" cy="46" r="8.5" stroke="currentColor" strokeWidth="2"/>
      <circle cx="92" cy="46" r="8.5" stroke="currentColor" strokeWidth="2"/>
      <circle cx="28" cy="46" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="92" cy="46" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  Sedans: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M8 44 L8 36 Q8 34 10 34 L16 34 L20 22 Q26 14 46 13 Q66 13 76 16 L86 22 L98 26 Q108 28 110 36 L110 44 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M20 34 L24 20 L50 13 L66 13 L76 22" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="24" y1="20" x2="76" y2="20" stroke="currentColor" strokeWidth="1.5"/>
      <line x1="76" y1="20" x2="86" y2="34" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M86 34 L86 28 L100 28 L100 34" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx="26" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="92" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="26" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="92" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  SUVs: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="10" y="18" width="100" height="28" rx="3" stroke="currentColor" strokeWidth="2"/>
      <line x1="10" y1="18" x2="110" y2="18" stroke="currentColor" strokeWidth="2"/>
      <line x1="22" y1="18" x2="22" y2="14" stroke="currentColor" strokeWidth="1.5"/>
      <line x1="98" y1="18" x2="98" y2="14" stroke="currentColor" strokeWidth="1.5"/>
      <line x1="22" y1="14" x2="98" y2="14" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10 18 L16 8 L104 8 L110 18" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="60" y1="18" x2="60" y2="46" stroke="currentColor" strokeWidth="1" strokeOpacity="0.35"/>
      <circle cx="28" cy="50" r="9" stroke="currentColor" strokeWidth="2"/>
      <circle cx="92" cy="50" r="9" stroke="currentColor" strokeWidth="2"/>
      <circle cx="28" cy="50" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="92" cy="50" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  Pickups: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M8 44 L8 28 Q8 24 12 24 L58 24 L58 44 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M12 24 L18 12 L54 12 L58 24" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M58 44 L58 30 L108 30 L108 44" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <line x1="58" y1="44" x2="108" y2="44" stroke="currentColor" strokeWidth="2"/>
      <line x1="108" y1="30" x2="108" y2="44" stroke="currentColor" strokeWidth="2"/>
      <circle cx="24" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="92" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="24" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="92" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  Coupes: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M6 46 Q6 38 10 36 L14 36 L22 20 Q30 10 52 10 Q70 10 80 14 L96 24 Q110 30 112 40 L112 46 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M22 20 L30 11 L56 10 L70 10 L80 20" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="30" y1="11" x2="70" y2="11" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M70 11 L80 20 L96 30 L104 40" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx="26" cy="48" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="96" cy="48" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="26" cy="48" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="96" cy="48" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  Wagons: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M8 44 L8 34 Q8 32 10 32 L16 32 L20 20 Q26 12 46 12 L96 12 L96 32 L110 32 L110 44 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <line x1="20" y1="20" x2="96" y2="20" stroke="currentColor" strokeWidth="1.5"/>
      <line x1="96" y1="12" x2="96" y2="44" stroke="currentColor" strokeWidth="2"/>
      <rect x="88" y="14" width="8" height="14" rx="1" stroke="currentColor" strokeWidth="1.2"/>
      <circle cx="26" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="92" cy="46" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="26" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="92" cy="46" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  Hatchbacks: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M14 46 Q14 38 18 36 L22 36 L28 20 Q36 12 56 12 Q74 12 82 16 L90 22 Q96 26 96 36 L108 36 L108 46 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M28 20 L34 13 L60 12 L74 12 L82 20" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="34" y1="13" x2="74" y2="13" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M74 13 L82 20 L90 28 L96 36" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <circle cx="30" cy="48" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="88" cy="48" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="30" cy="48" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="88" cy="48" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  Convertibles: (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <path d="M8 46 Q8 38 12 36 L16 36 L20 30 Q24 26 36 26 L84 26 Q96 26 100 30 L104 36 L112 36 L112 46 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M36 26 L40 16 L80 16 L84 26" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <circle cx="26" cy="48" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="94" cy="48" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="26" cy="48" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="94" cy="48" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  'Cargo Vans': (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="8" y="12" width="104" height="34" rx="2" stroke="currentColor" strokeWidth="2"/>
      <path d="M8 12 L8 8 Q8 6 10 6 L30 6 L36 12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <line x1="36" y1="12" x2="36" y2="46" stroke="currentColor" strokeWidth="2"/>
      <circle cx="22" cy="50" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="96" cy="50" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="22" cy="50" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="96" cy="50" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  'Box Trucks': (
    <svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="44" y="6" width="70" height="40" rx="1" stroke="currentColor" strokeWidth="2"/>
      <path d="M6 46 L6 28 Q6 24 10 24 L46 24 L46 46 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M10 24 L14 14 L42 14 L46 24" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <circle cx="20" cy="50" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="96" cy="50" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="20" cy="50" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="96" cy="50" r="3" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
};

function SegmentCard({ segment, index }: { segment: Segment; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { lang } = useLanguage();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTimeout(() => setVisible(true), index * 80); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={ref}
      className="group relative flex flex-col items-center p-2 sm:p-3 rounded-xl cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? (hovered ? 'translateY(-4px)' : 'translateY(0px) scale(1)') : 'translateY(32px) scale(0.94)',
        transition: `opacity 0.55s cubic-bezier(0.22,1,0.36,1) ${index * 80}ms, transform 0.3s cubic-bezier(0.22,1,0.36,1)`,
        background: hovered ? 'rgba(196,168,130,0.10)' : 'rgba(196,168,130,0.04)',
        border: `1px solid ${hovered ? 'rgba(196,168,130,0.25)' : 'rgba(196,168,130,0.12)'}`,
      }}
    >
      {segment.badge && (
        <div className="absolute -top-1.5 -right-1.5 w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center z-10" style={{ background: segment.badge.bg, border: `1.5px solid ${segment.badge.color}55` }}>
          <i className={`${segment.badge.icon} text-[7px] sm:text-[8px]`} style={{ color: segment.badge.color }}></i>
        </div>
      )}
      <div className="w-full h-11 transition-opacity duration-300" style={{ color: '#c4a882', opacity: hovered ? 0.9 : 0.55, paddingBottom: '6px' }}>
        {silhouettes[segment.nameEn]}
      </div>
      <div className="w-4/5 h-px mb-1.5" style={{ background: 'rgba(196,168,130,0.1)' }}></div>
      <div className="text-[8px] sm:text-[10px] font-semibold tracking-tight leading-tight text-center" style={{ color: hovered ? '#c4a882' : 'rgba(196,168,130,0.6)', fontFamily: 'Satoshi, Inter, sans-serif' }}>
        {lang === 'en' ? segment.nameEn : segment.nameFr}
      </div>
    </div>
  );
}

function EVCallout({ visible }: { visible: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { lang } = useLanguage();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setShow(true); observer.disconnect(); } }, { threshold: 0.15 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pillars = [
    { icon: 'ri-battery-charge-line', titleEn: 'Range Anxiety', titleFr: "Anxiété d'autonomie", descEn: 'We match you with EVs whose real-world range fits your daily drive — no guesswork, no surprises at 10%.', descFr: "Nous vous jumelons avec des VÉ dont l'autonomie réelle correspond à votre conduite quotidienne — aucune supposition, aucune surprise à 10 %." },
    { icon: 'ri-charging-pile-2-line', titleEn: 'Charging Infrastructure', titleFr: 'Infrastructure de recharge', descEn: 'Home charger compatibility, public network coverage, and Level 2 vs. DC fast-charge — all factored in.', descFr: 'Compatibilité du chargeur domestique, couverture du réseau public et compromis entre niveau 2 et recharge rapide CC — tout est pris en compte.' },
    { icon: 'ri-money-dollar-circle-line', titleEn: 'Incentives & Tax Credits', titleFr: "Incitatifs et crédits d'impôt", descEn: 'Federal, provincial, and utility rebates are complex. We surface every dollar you qualify for before you sign.', descFr: 'Les rabais fédéraux, provinciaux et des services publics sont complexes. Nous révélons chaque dollar auquel vous avez droit avant de signer.' },
  ];

  return (
    <div ref={ref} className="mt-10 sm:mt-16" style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0px)' : 'translateY(36px)', transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s' }}>
      <div className="rounded-2xl sm:rounded-3xl overflow-hidden relative" style={{ background: 'rgba(8,8,20,0.8)', border: '1px solid rgba(196,168,130,0.2)', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, rgba(196,168,130,0.3), rgba(232,216,192,0.6), rgba(196,168,130,0.3))' }}></div>
        <div className="px-4 py-5 sm:px-8 sm:py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-6">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(232,216,192,0.08)', border: '1px solid rgba(232,216,192,0.2)' }}>
                <i className="ri-flashlight-fill text-[#e8d8c0] text-xs sm:text-base"></i>
              </div>
              <h3 className="text-base sm:text-2xl lg:text-3xl font-black text-white leading-tight" style={{ fontFamily: 'Satoshi, Inter, sans-serif' }}>
                {lang === 'en' ? <>How We Handle <span style={{ color: '#c4a882' }}>Electric Vehicles</span></> : <>Comment nous gérons <span style={{ color: '#c4a882' }}>les véhicules électriques</span></>}
              </h3>
            </div>
            <button onClick={() => setExpanded(v => !v)} className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-[10px] sm:text-xs font-bold transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0" style={{ background: expanded ? 'rgba(196,168,130,0.12)' : 'rgba(196,168,130,0.06)', border: `1px solid ${expanded ? 'rgba(196,168,130,0.35)' : 'rgba(196,168,130,0.2)'}`, color: '#c4a882' }}>
              <span>{lang === 'en' ? (expanded ? 'Hide considerations' : 'Show buying considerations') : (expanded ? 'Masquer' : 'Voir les considérations')}</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}></i>
            </button>
          </div>

          <div style={{ maxHeight: expanded ? '600px' : '0px', overflow: 'hidden', transition: 'max-height 0.5s cubic-bezier(0.22,1,0.36,1)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5 mt-5 sm:mt-7">
              {pillars.map((p, i) => (
                <div key={i} className="rounded-xl sm:rounded-2xl p-4 sm:p-6 cursor-default" style={{ background: 'rgba(196,168,130,0.04)', border: '1px solid rgba(196,168,130,0.1)', opacity: expanded ? 1 : 0, transform: expanded ? 'translateY(0px)' : 'translateY(12px)', transition: `opacity 0.4s ease ${0.5 + i * 0.1}s, transform 0.4s ease ${0.5 + i * 0.1}s` }}>
                  <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center mb-3 sm:mb-4" style={{ background: 'rgba(196,168,130,0.08)', border: '1px solid rgba(196,168,130,0.18)' }}>
                    <i className={`${p.icon} text-base sm:text-xl text-[#c4a882]`}></i>
                  </div>
                  <h4 className="text-sm sm:text-lg font-bold text-white mb-1.5 sm:mb-2" style={{ fontFamily: 'Satoshi, Inter, sans-serif' }}>
                    {lang === 'en' ? p.titleEn : p.titleFr}
                  </h4>
                  <p className="text-[10px] sm:text-sm leading-relaxed" style={{ color: 'rgba(196,168,130,0.5)' }}>
                    {lang === 'en' ? p.descEn : p.descFr}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom stats */}
          <div className="mt-5 sm:mt-7 pt-4 sm:pt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-8" style={{ borderTop: '1px solid rgba(196,168,130,0.1)' }}>
            {(lang === 'en'
              ? [{ value: '75+', label: 'EV models analyzed' }, { value: '$7,500', label: 'avg. federal credit surfaced' }, { value: '98%', label: 'range-match accuracy' }]
              : [{ value: '75+', label: 'modèles de VÉ analysés' }, { value: '7 500 $', label: 'crédit fédéral moyen révélé' }, { value: '98 %', label: "précision de correspondance d'autonomie" }]
            ).map((stat, i) => (
              <div key={i} className="flex items-center gap-2 sm:gap-3">
                <span className="text-lg sm:text-2xl font-black" style={{ color: '#c4a882', fontFamily: 'Satoshi, Inter, sans-serif', opacity: show ? 1 : 0, transition: `opacity 0.5s ease ${0.6 + i * 0.1}s` }}>{stat.value}</span>
                <span className="text-[9px] sm:text-xs leading-tight max-w-[80px]" style={{ color: 'rgba(196,168,130,0.4)' }}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VehicleSegmentsSection() {
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);
  const { lang } = useLanguage();

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setHeaderVisible(true); observer.disconnect(); } }, { threshold: 0.2 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative py-6 sm:py-10 lg:py-14 overflow-hidden" style={{ background: '#08080f' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(196,168,130,0.06) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(196,168,130,0.03) 0%, transparent 65%)' }} />
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(196,168,130,0.15), transparent)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-20">
        <div ref={headerRef} className="text-left sm:text-center mb-5 sm:mb-8" style={{ opacity: headerVisible ? 1 : 0, transform: headerVisible ? 'translateY(0px)' : 'translateY(28px)', transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)' }}>
          <h2 className="text-[17px] sm:text-[28px] lg:text-4xl font-black text-white mb-2 sm:mb-3 tracking-tight" style={{ fontFamily: 'Satoshi, Inter, sans-serif' }}>
            {lang === 'en' ? 'Vehicle Segments We Analyze \u0026 Match' : 'Segments que nous analysons et jumelons'}
          </h2>
          <p className="text-[11px] sm:text-sm max-w-xl mx-auto" style={{ color: 'rgba(196,168,130,0.5)' }}>
            {lang === 'en'
              ? 'Our expertise spans every segment of the market. New, used, personal, commercial, finance, lease & purchase.'
              : 'Notre expertise couvre tous les segments du marché : neuf, occasion, particulier, professionnel, financement, location et achat.'}
          </p>
        </div>

        <div className="grid grid-cols-6 gap-1.5 sm:gap-2.5 mb-2">
          {segments.slice(0, 6).map((seg, i) => <SegmentCard key={lang === 'en' ? seg.nameEn : seg.nameFr} segment={seg} index={i} />)}
        </div>
        <div className="grid grid-cols-6 gap-1.5 sm:gap-2.5 max-w-4xl mx-auto">
          {segments.slice(6).map((seg, i) => <SegmentCard key={lang === 'en' ? seg.nameEn : seg.nameFr} segment={seg} index={i + 6} />)}
        </div>

        <EVCallout visible={headerVisible} />
      </div>
    </section>
  );
}
